import React, { useState, useRef, useEffect } from 'react';
import { Maximize2, X } from 'lucide-react';

// Global video manager to handle auto-pause functionality
const videoManager = {
  videos: new Set(),
  pauseAllExcept: (currentVideo) => {
    videoManager.videos.forEach(video => {
      if (video !== currentVideo && !video.paused) {
        const pausePromise = video.pause();
        if (pausePromise !== undefined) {
          pausePromise.catch(() => {
            // Pause error, ignore
          });
        }
      }
    });
  }
};

const MediaAsset = ({ url, title, type = 'auto' }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef(null);

  // Determine media type from URL or props
  const getMediaType = () => {
    if (type !== 'auto') return type;
    
    // Remove query parameters and get the path
    const urlPath = url.split('?')[0];
    const extension = urlPath.split('.').pop()?.toLowerCase();
    
    // Check for video extensions
    if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(extension)) {
      return 'video';
    }
    // Check for image extensions
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
      return 'image';
    }
    
    // Fallback: check URL path for hints
    if (urlPath.includes('/dwnld/video/')) {
      return 'video';
    }
    if (urlPath.includes('/dwnld/image/')) {
      return 'image';
    }
    
    return 'unknown';
  };

  const mediaType = getMediaType();

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Video management effect
  useEffect(() => {
    if (videoRef.current && mediaType === 'video') {
      const video = videoRef.current;
      
      // Add video to manager
      videoManager.videos.add(video);
      
      // Handle play event to pause other videos
      const handlePlay = () => {
        videoManager.pauseAllExcept(video);
      };
      
      video.addEventListener('play', handlePlay);
      
      // Cleanup
      return () => {
        videoManager.videos.delete(video);
        video.removeEventListener('play', handlePlay);
      };
    }
  }, [mediaType]);

  // Add keyboard event listener for Escape key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isFullscreen]);

  if (mediaType === 'video') {
    console.log('Rendering video component with URL:', url);
    return (
      <div className="relative group">
        <video
          ref={videoRef}
          className="w-full aspect-[9/16] rounded-lg shadow-md"
          controls
          preload="metadata"
          onError={(e) => {
            console.error('Failed to load video:', url, e);
            e.target.style.display = 'none';
          }}
          onLoadStart={() => console.log('Video loading started:', url)}
          onLoadedData={() => console.log('Video loaded successfully:', url)}
        >
          <source src={url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {title && (
          <div className="mt-2 text-sm text-slate-200 font-medium">
            {title}
          </div>
        )}
        

      </div>
    );
  }

  if (mediaType === 'image') {
    console.log('Rendering image component with URL:', url);
    return (
      <>
        <div className="relative group">
          <img
            src={url}
            alt={title || 'Ad asset'}
            className="w-full aspect-[9/16] object-cover rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow"
            onClick={handleFullscreen}
            onError={(e) => {
              console.error('Failed to load image:', url, e);
              e.target.style.display = 'none';
            }}
            onLoad={() => console.log('Image loaded successfully:', url)}
          />
          
          {title && (
            <div className="mt-2 text-sm text-slate-200 font-medium">
              {title}
            </div>
          )}
          
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={handleFullscreen}
              className="p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Fullscreen Modal */}
        {isFullscreen && (
          <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
            <div className="relative w-full h-full flex items-center justify-center overflow-auto">
              <div className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center">
                <img
                  src={url}
                  alt={title || 'Ad asset'}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  style={{ maxWidth: '90vw', maxHeight: '90vh' }}
                />
                <button
                  onClick={handleFullscreen}
                  className="absolute top-4 right-4 p-3 bg-black bg-opacity-70 text-white rounded-full hover:bg-opacity-90 transition-all z-10"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Fallback for unknown media types or failed loads
  return (
    <div className="p-4 bg-slate-700 rounded-lg border border-slate-600 min-h-[200px] flex items-center justify-center">
      <div className="flex items-center space-x-2">
        <div className="w-8 h-8 bg-slate-600 rounded flex items-center justify-center">
          <span className="text-xs text-slate-300">?</span>
        </div>
        <div>
          <div className="text-sm font-medium text-slate-200">
            {title || 'Media Asset'}
          </div>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-purple-400 hover:text-purple-300 underline"
          >
            View Asset
          </a>
          <div className="text-xs text-slate-400 mt-1">
            {mediaType === 'unknown' ? 'Unknown format' : 'Failed to load'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaAsset; 